<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import MetricCard from '../components/MetricCard.vue';
import ChannelDonutChart from '../components/ChannelDonutChart.vue';
import TopInboxesList from '../components/TopInboxesList.vue';
import LabelsCloud from '../components/LabelsCloud.vue';
import ChannelTypeComparisonChart from '../components/ChannelTypeComparisonChart.vue';
import InboxResponseTimeChart from '../components/InboxResponseTimeChart.vue';
import InboxCsatChart from '../components/InboxCsatChart.vue';
import InboxWorkloadBalanceChart from '../components/InboxWorkloadBalanceChart.vue';
import BotVsHumanChart from '../components/BotVsHumanChart.vue';
import ContactSourcesCard from '../components/ContactSourcesCard.vue';
import InboxDailyEvolutionChart from '../components/InboxDailyEvolutionChart.vue';
import InboxSlaCard from '../components/InboxSlaCard.vue';
import InboxDetailedTable from '../components/InboxDetailedTable.vue';
import HourlyBarChart from '../components/HourlyBarChart.vue';

const { t } = useI18n();

const {
  inboxReports,
  labelReports,
  inboxes,
  labels,
  inboxesDetailed: inboxesDetailedData,
  isLoading,
} = inject('analyticsData');

// Use detailed data from parent (already fetched centrally with 5s refresh)
const detailedData = computed(() => inboxesDetailedData.value);

// Format large numbers
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

// Format time
function formatMinutes(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} h`;
}

// Summary computed values from detailed data
const summary = computed(() => detailedData.value?.summary || {});
const inboxesDetailed = computed(() => detailedData.value?.inboxes || []);
const byChannelType = computed(() => detailedData.value?.by_channel_type || []);
const hourlyDistribution = computed(
  () => detailedData.value?.hourly_distribution || []
);
const dailyEvolution = computed(
  () => detailedData.value?.daily_evolution || []
);
const responseTimesByInbox = computed(
  () => detailedData.value?.response_times_by_inbox || []
);
const csatByInbox = computed(() => detailedData.value?.csat_by_inbox || []);
const slaByInbox = computed(() => detailedData.value?.sla_by_inbox || []);
const botVsHuman = computed(() => detailedData.value?.bot_vs_human || []);
const contactSources = computed(
  () => detailedData.value?.contact_sources || {}
);
const workloadBalance = computed(
  () => detailedData.value?.workload_balance || {}
);

// Fallback to basic data
const totalChannels = computed(() => {
  return (
    summary.value.total_inboxes ||
    inboxReports.value?.length ||
    inboxes.value?.length ||
    0
  );
});

const activeChannels = computed(() => {
  return (
    summary.value.active_inboxes ||
    inboxReports.value?.filter(i => (i.conversations_count || 0) > 0).length ||
    0
  );
});

const totalLabels = computed(() => {
  return labelReports.value?.length || labels.value?.length || 0;
});

// Channel distribution for donut chart
const channelDistribution = computed(() => {
  if (byChannelType.value.length > 0) {
    return byChannelType.value.map(c => ({
      label: c.channel_name,
      value: c.conversations_count,
      color: c.color,
    }));
  }

  // Fallback to basic data
  const data =
    inboxReports.value?.length > 0 ? inboxReports.value : inboxes.value;
  if (!data || data.length === 0) return [];

  const typeGroups = {};
  data.forEach(inbox => {
    const type = inbox.channel_type || 'other';
    if (!typeGroups[type]) {
      typeGroups[type] = 0;
    }
    typeGroups[type] += inbox.conversations_count || 1;
  });

  const colorMap = {
    'Channel::WebWidget': '#3b82f6',
    'Channel::Api': '#8b5cf6',
    'Channel::Whatsapp': '#22c55e',
    'Channel::FacebookPage': '#1877f2',
    'Channel::TwitterProfile': '#1da1f2',
    'Channel::Email': '#f59e0b',
    'Channel::Telegram': '#0088cc',
    other: '#6b7280',
  };

  const labelMap = {
    'Channel::WebWidget': 'Web Widget',
    'Channel::Api': 'API',
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::FacebookPage': 'Facebook',
    'Channel::TwitterProfile': 'Twitter',
    'Channel::Email': 'Email',
    'Channel::Telegram': 'Telegram',
    other: 'Outros',
  };

  return Object.entries(typeGroups)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({
      label: labelMap[type] || type.replace('Channel::', ''),
      value: count,
      color: colorMap[type] || '#6b7280',
    }))
    .sort((a, b) => b.value - a.value);
});

// Top inboxes list
const topInboxes = computed(() => {
  if (inboxesDetailed.value.length > 0) {
    return inboxesDetailed.value.slice(0, 5).map(inbox => ({
      id: inbox.id,
      name: inbox.name,
      channelType: inbox.channel_type,
      conversations: inbox.conversations_count || 0,
    }));
  }

  if (inboxReports.value?.length > 0) {
    return [...inboxReports.value]
      .sort(
        (a, b) => (b.conversations_count || 0) - (a.conversations_count || 0)
      )
      .slice(0, 5)
      .map(inbox => ({
        id: inbox.id,
        name: inbox.name,
        channelType: inbox.channel_type,
        conversations: inbox.conversations_count || 0,
      }));
  }

  if (!inboxes.value || inboxes.value.length === 0) return [];
  return [...inboxes.value].slice(0, 5).map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    channelType: inbox.channel_type,
    conversations: 0,
  }));
});

// Most used labels
const topLabels = computed(() => {
  if (labelReports.value?.length > 0) {
    return [...labelReports.value]
      .sort(
        (a, b) => (b.conversations_count || 0) - (a.conversations_count || 0)
      )
      .slice(0, 10)
      .map(label => ({
        id: label.id,
        title: label.title,
        color: label.color,
        count: label.conversations_count || 0,
      }));
  }

  if (!labels.value || labels.value.length === 0) return [];
  return labels.value.slice(0, 10).map(label => ({
    id: label.id,
    title: label.title,
    color: label.color,
    count: label.conversations_count || 0,
  }));
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Main Metrics Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <MetricCard
        icon="i-lucide-inbox"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.TOTAL_CHANNELS')"
        :value="String(totalChannels)"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-zap"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.ACTIVE_CHANNELS')"
        :value="String(activeChannels)"
        color="green"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-message-square"
        title="Total Conversas"
        :value="formatNumber(summary.total_conversations || 0)"
        color="blue"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-check-circle"
        title="Resolvidas"
        :value="formatNumber(summary.total_resolved || 0)"
        :subtitle="`${summary.resolution_rate || 0}% taxa`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-timer"
        title="Tempo Medio Resp."
        :value="formatMinutes(summary.avg_first_response_minutes)"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-tag"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.LABELS')"
        :value="String(totalLabels)"
        color="purple"
        :loading="isLoading"
      />
    </div>

    <!-- Second Row: CSAT, SLA, Messages -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <MetricCard
        icon="i-lucide-star"
        title="CSAT Medio"
        :value="`${summary.avg_csat_score || 0}%`"
        :subtitle="`${summary.total_csat_responses || 0} avaliacoes`"
        color="amber"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-shield-check"
        title="SLA Conformidade"
        :value="`${summary.sla_compliance || 0}%`"
        :subtitle="`${summary.sla_total || 0} total`"
        color="teal"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-message-circle"
        title="Total Mensagens"
        :value="formatNumber(summary.total_messages || 0)"
        :subtitle="`${summary.avg_messages_per_conversation || 0} por conv.`"
        color="violet"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-users"
        title="Contatos Unicos"
        :value="formatNumber(contactSources.total_unique_contacts || 0)"
        :subtitle="`${contactSources.new_contacts || 0} novos`"
        color="blue"
        :loading="isLoading"
      />
    </div>

    <!-- Daily Evolution Chart (Full Width) -->
    <InboxDailyEvolutionChart
      :data="dailyEvolution"
      :loading="isLoading"
      title="Evolucao Diaria de Conversas"
    />

    <!-- Charts Row 1: Channel Distribution and Top Inboxes -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChannelDonutChart
        :data="channelDistribution"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.BY_CHANNEL')"
      />
      <TopInboxesList
        :data="topInboxes"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.TOP_INBOXES')"
      />
    </div>

    <!-- Channel Type Comparison (Full Width) -->
    <ChannelTypeComparisonChart
      :data="byChannelType"
      :loading="isLoading"
      title="Comparativo por Tipo de Canal"
    />

    <!-- Charts Row 2: Hourly Distribution and Workload Balance -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <HourlyBarChart
        :data="hourlyDistribution"
        :loading="isLoading"
        title="Distribuicao por Hora do Dia"
      />
      <InboxWorkloadBalanceChart
        :data="workloadBalance"
        :loading="isLoading"
        title="Balanceamento de Carga por Canal"
      />
    </div>

    <!-- Charts Row 3: Response Times and CSAT -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <InboxResponseTimeChart
        :data="responseTimesByInbox"
        :loading="isLoading"
        title="Tempo de 1a Resposta por Canal"
      />
      <InboxCsatChart
        :data="csatByInbox"
        :loading="isLoading"
        title="CSAT por Canal"
      />
    </div>

    <!-- Charts Row 4: SLA and Bot vs Human -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <InboxSlaCard
        :data="slaByInbox"
        :loading="isLoading"
        title="Conformidade SLA por Canal"
      />
      <BotVsHumanChart
        :data="botVsHuman"
        :loading="isLoading"
        title="Bot vs Humano por Canal"
      />
    </div>

    <!-- Charts Row 5: Contact Sources and Labels -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ContactSourcesCard
        :data="contactSources"
        :loading="isLoading"
        title="Origem dos Contatos"
      />
      <LabelsCloud
        :data="topLabels"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.CHANNELS.MOST_USED_LABELS')"
      />
    </div>

    <!-- Detailed Inboxes Table -->
    <InboxDetailedTable
      :data="inboxesDetailed"
      :loading="isLoading"
      title="Relatorio Detalhado por Canal"
    />
  </div>
</template>
